/**
 * useLayerKeyboardShortcuts Hook
 *
 * Provides keyboard shortcuts for layer drawer operations.
 *
 * Shortcuts:
 * - Escape: Exit selection mode
 * - Ctrl/Cmd + A: Select all layers (when in selection mode)
 * - Ctrl/Cmd + Shift + E: Enable selected layers
 * - Ctrl/Cmd + Shift + D: Disable selected layers
 * - Ctrl/Cmd + S: Save preset (when drawer is open)
 * - Ctrl/Cmd + O: Open/load preset (when drawer is open)
 */

import { useEffect, useCallback } from 'react';
import { useLayerDrawer } from '../LayerDrawerContext';
import { DrawerHeight } from '../utils/layerDrawerUtils';

interface UseLayerKeyboardShortcutsOptions {
  enabled?: boolean;
  onSavePreset?: () => void;
  onLoadPreset?: () => void;
}

export const useLayerKeyboardShortcuts = ({
  enabled = true,
  onSavePreset,
  onLoadPreset
}: UseLayerKeyboardShortcutsOptions = {}) => {
  const {
    drawerHeight,
    selectionMode,
    selectedLayerIds,
    toggleSelectionMode,
    selectAllLayers,
    deselectAllLayers,
    enableSelectedLayers,
    disableSelectedLayers
  } = useLayerDrawer();

  const isDrawerOpen = drawerHeight !== DrawerHeight.HIDDEN;

  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    // Only handle shortcuts when drawer is open
    if (!isDrawerOpen) return;

    // Check for modifier keys
    const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
    const isModifierPressed = isMac ? event.metaKey : event.ctrlKey;

    // Escape - Exit selection mode
    if (event.key === 'Escape' && selectionMode) {
      event.preventDefault();
      toggleSelectionMode();
      return;
    }

    // Ctrl/Cmd + A - Select all (only in selection mode)
    if (isModifierPressed && event.key === 'a' && selectionMode) {
      event.preventDefault();
      if (selectedLayerIds.size > 0) {
        deselectAllLayers();
      } else {
        selectAllLayers();
      }
      return;
    }

    // Ctrl/Cmd + Shift + E - Enable selected layers
    if (isModifierPressed && event.shiftKey && event.key === 'E' && selectionMode) {
      event.preventDefault();
      if (selectedLayerIds.size > 0) {
        enableSelectedLayers();
      }
      return;
    }

    // Ctrl/Cmd + Shift + D - Disable selected layers
    if (isModifierPressed && event.shiftKey && event.key === 'D' && selectionMode) {
      event.preventDefault();
      if (selectedLayerIds.size > 0) {
        disableSelectedLayers();
      }
      return;
    }

    // Ctrl/Cmd + S - Save preset (if callback provided)
    if (isModifierPressed && event.key === 's' && onSavePreset && !selectionMode) {
      event.preventDefault();
      onSavePreset();
      return;
    }

    // Ctrl/Cmd + O - Open/load preset (if callback provided)
    if (isModifierPressed && event.key === 'o' && onLoadPreset && !selectionMode) {
      event.preventDefault();
      onLoadPreset();
      return;
    }
  }, [
    isDrawerOpen,
    selectionMode,
    selectedLayerIds,
    toggleSelectionMode,
    selectAllLayers,
    deselectAllLayers,
    enableSelectedLayers,
    disableSelectedLayers,
    onSavePreset,
    onLoadPreset
  ]);

  useEffect(() => {
    if (!enabled) return;

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [enabled, handleKeyDown]);

  return {
    shortcuts: [
      { key: 'Escape', description: 'Exit selection mode', condition: 'Selection mode' },
      { key: 'Ctrl/Cmd + A', description: 'Toggle select all', condition: 'Selection mode' },
      { key: 'Ctrl/Cmd + Shift + E', description: 'Enable selected', condition: 'Selection mode' },
      { key: 'Ctrl/Cmd + Shift + D', description: 'Disable selected', condition: 'Selection mode' },
      { key: 'Ctrl/Cmd + S', description: 'Save preset', condition: 'Drawer open' },
      { key: 'Ctrl/Cmd + O', description: 'Load preset', condition: 'Drawer open' }
    ]
  };
};

export default useLayerKeyboardShortcuts;
